import { useCallback, useEffect, useRef, useState } from 'react';

interface ScrollEventWrapperProps {
  id?: string;
  children: React.ReactNode;
  onThresholdPass: (key?: unknown | unknown[]) => void;
  onAlwaysThresholdPass?: (key?: unknown | unknown[]) => void;
  once: boolean;
  sendInterSectionState?: (state: boolean) => void;
  threshold?: number;
  delayTime?: number;
}

const IntersectionObserverWrapper = ({
  id,
  children,
  onThresholdPass,
  onAlwaysThresholdPass,
  once,
  sendInterSectionState,
  threshold = 0.2,
  delayTime = 0
}: ScrollEventWrapperProps) => {
  const target = useRef<HTMLDivElement>(null);
  const onceRef = useRef<boolean>(false);
  const timeoutKey = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [isExecutedAlwaysThreshold, setIsExecutedAlwaysThreshold] = useState(false);

  const onIntersect = useCallback(
    async ([entry]: IntersectionObserverEntry[]) => {
      if (!entry) return;

      setIsIntersecting(entry.isIntersecting);
      if (sendInterSectionState) {
        sendInterSectionState(entry.isIntersecting);
      }
    },
    [sendInterSectionState]
  );

  useEffect(() => {
    if (isIntersecting) {
      onAlwaysThresholdPass?.();
      setIsExecutedAlwaysThreshold(true);
    }
  }, [isIntersecting, onAlwaysThresholdPass]);

  useEffect(() => {
    if (isIntersecting && isExecutedAlwaysThreshold && (!once || (once && !onceRef.current))) {
      const executeThresholdPass = () => {
        onThresholdPass?.();
        setIsExecutedAlwaysThreshold(false);
        if (once) {
          onceRef.current = true;
        }
      };

      if (delayTime > 0) {
        timeoutKey.current = setTimeout(executeThresholdPass, delayTime);
      } else {
        executeThresholdPass();
      }
    }

    return () => {
      if (timeoutKey.current) {
        clearTimeout(timeoutKey.current);
        timeoutKey.current = null;
      }
    };
  }, [delayTime, isExecutedAlwaysThreshold, isIntersecting, onThresholdPass, once]);

  useEffect(() => {
    let observer: IntersectionObserver;
    if (!once || (once && !onceRef.current)) {
      observer = new IntersectionObserver(onIntersect, { threshold });
      setTimeout(() => {
        if (target.current) {
          observer.observe(target.current);
        }
      }, 1000);
    }
    return () => observer && observer.disconnect();
  }, [onIntersect, once, threshold]);

  return (
    <div id={id} ref={target}>
      {children}
    </div>
  );
};

export default IntersectionObserverWrapper;
